<!--
  Client Portal Settings -- Company section.

  Jake 2026-10-27: "there's no place anywhere in the app to change a client
  company name, not as the client owner, not as the distributor, and not as
  SA." Decision: the client's owner renames its own company in a Company
  section of the portal Settings page. Owner-only.

  Wired to:
    GET   /api/portal/company   -> { name, can_edit }
    PATCH /api/portal/company   { name } -> { name, can_edit }
  Both are scoped server-side to the CALLER's own client (never a client id
  from the request). `can_edit` comes from the server (portal.manage_company,
  held by the Client Admin) and the PATCH enforces it again; a Store Manager
  sees the name read-only. The server trims, requires a non-empty name of at
  most 255 characters with no control or text-direction characters, and
  answers 409 ("That name is not available.") when another client of the same
  distributor already has the name -- its message is shown as-is. Ten 409s in
  an hour spend the account's budget and the PATCH answers 429.

  Renaming changes the live name everywhere it is read. Past orders keep the
  ship-to they were placed with.
-->
<template>
    <v-container fluid class="portal-settings" data-testid="portal-page-settings">
        <header class="ps-header">
            <div class="ps-eyebrow">Company-wide</div>
            <h1 class="text-h5 mb-1">Settings</h1>
        </header>

        <section class="ps-card" data-testid="portal-company-section">
            <h2 class="ps-card__title">Company</h2>

            <v-progress-linear
                v-if="loading"
                indeterminate
                color="primary"
                data-testid="portal-company-loading"
            />

            <p
                v-else-if="loadError"
                class="ps-error"
                role="alert"
                data-testid="portal-company-load-error"
            >
                {{ loadError }}
            </p>

            <form
                v-else-if="canEdit"
                class="ps-form"
                novalidate
                data-testid="portal-company-form"
                @submit.prevent="save"
            >
                <label
                    :for="inputId"
                    class="ps-label"
                    data-testid="portal-company-name-label"
                >Company name</label>
                <div class="ps-row">
                    <input
                        :id="inputId"
                        v-model="draft"
                        type="text"
                        maxlength="255"
                        autocomplete="organization"
                        class="ps-input"
                        :aria-invalid="error ? 'true' : 'false'"
                        :aria-describedby="error ? errorId : undefined"
                        data-testid="portal-company-name-input"
                        @input="onInput"
                    />
                    <PortalButton
                        type="submit"
                        variant="primary"
                        :disabled="!dirty || saving"
                        :loading="saving"
                        data-testid="portal-company-save-btn"
                    >
                        Save
                    </PortalButton>
                </div>
                <p
                    v-if="error"
                    :id="errorId"
                    class="ps-error"
                    role="alert"
                    data-testid="portal-company-error"
                >
                    {{ error }}
                </p>
                <p
                    class="ps-status"
                    aria-live="polite"
                    data-testid="portal-company-status"
                >
                    {{ saved ? "Saved" : "" }}
                </p>
                <p class="ps-hint">
                    This is how your company appears to your distributor and on
                    new orders. Orders already placed keep the details they were
                    placed with.
                </p>
            </form>

            <div v-else>
                <div class="ps-label">Company name</div>
                <p class="ps-value" data-testid="portal-company-name">
                    {{ name }}
                </p>
                <p class="ps-hint" data-testid="portal-company-readonly-note">
                    Only a Client Admin on your account can change the company
                    name.
                </p>
            </div>
        </section>
    </v-container>
</template>

<script>
import axios from "@/axios";
import PortalButton from "@/components/portal/PortalButton.vue";

const COMPANY_URL = "/api/portal/company";

export default {
    name: "PortalSettings",
    components: { PortalButton },
    data() {
        return {
            loading: true,
            loadError: "",
            // The name the server has; `draft` is what the input holds.
            name: "",
            draft: "",
            canEdit: false,
            saving: false,
            saved: false,
            error: "",
            inputId: "portal-company-name-input",
            errorId: "portal-company-name-error",
        };
    },
    computed: {
        dirty() {
            return (this.draft || "").trim() !== this.name;
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        async load() {
            this.loading = true;
            this.loadError = "";
            try {
                const resp = await axios.get(COMPANY_URL);
                this.applyServer(resp?.data || {});
            } catch (e) {
                this.loadError = "We could not load your company settings. Try again later.";
            } finally {
                this.loading = false;
            }
        },
        applyServer(data) {
            this.name = data.name || "";
            this.draft = this.name;
            this.canEdit = Boolean(data.can_edit);
        },
        onInput() {
            this.saved = false;
            this.error = "";
        },
        async save() {
            const next = (this.draft || "").trim();
            if (!next) {
                this.error = "Company name is required.";
                return;
            }
            if (next === this.name) return;
            this.saving = true;
            this.error = "";
            this.saved = false;
            try {
                const resp = await axios.patch(COMPANY_URL, { name: next });
                this.applyServer(resp?.data || { name: next, can_edit: true });
                this.saved = true;
            } catch (e) {
                // 429: the account spent its budget of unavailable names
                // (PATCH answers 409 for a taken name; ten in an hour and
                // the route refuses until the window rolls). Flask-Limiter's
                // 429 carries no JSON message, so the page says it here.
                this.error =
                    e?.response?.status === 429
                        ? "Too many names were not available. Wait a while, then try again."
                        : e?.response?.data?.message ||
                          "We could not save the company name. Try again.";
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.portal-settings {
    max-width: 880px;
}
.ps-header {
    margin-bottom: 16px;
}
.ps-eyebrow {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--tf-fg-3, #6b7280);
}
.ps-card {
    background: var(--tf-bg, #fff);
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-sm, 6px);
    padding: 20px;
}
.ps-card__title {
    margin: 0 0 12px;
    font-size: 14px;
    font-weight: 600;
    color: var(--tf-fg-2);
    text-transform: uppercase;
    letter-spacing: 0.05em;
}
.ps-form {
    display: flex;
    flex-direction: column;
    gap: 6px;
}
.ps-label {
    font-size: 12px;
    font-weight: 600;
    color: var(--tf-fg-2);
}
.ps-row {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
}
.ps-input {
    flex: 1;
    min-width: 220px;
    max-width: 480px;
    padding: 8px 10px;
    border: 1px solid var(--tf-border, #d0d4dc);
    border-radius: var(--tf-radius-xs, 4px);
    background: var(--tf-bg, #fff);
    color: var(--tf-fg-1);
    font-family: inherit;
    font-size: 14px;
}
.ps-input:focus {
    border-color: var(--portal-brand-primary, #3b82f6);
    outline: none;
}
.ps-input[aria-invalid="true"] {
    border-color: #c0392b;
}
.ps-value {
    margin: 4px 0 8px;
    font-size: 15px;
    color: var(--tf-fg-1);
}
.ps-error {
    margin: 0;
    font-size: 12px;
    color: #c0392b;
}
.ps-status {
    margin: 0;
    min-height: 16px;
    font-size: 12px;
    color: var(--tf-fg-3, #6b7280);
}
.ps-hint {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--tf-fg-3, #6b7280);
}
</style>
